// src/Components/Hero.js
import { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import styles from "../styles/Hero.module.css";
import confetti from "canvas-confetti";
import { api } from "../lib/api";

import { useAuth } from "../auth/AuthContext";
import SubscribeModal from "./SubscribeModal";

const calculateChineseZodiacAndElement = (year) => {
  const animals = [
    "Rat", "Ox", "Tiger", "Rabbit", "Dragon", "Snake",
    "Horse", "Goat", "Monkey", "Rooster", "Dog", "Pig",
  ];
  const elements = ["Metal", "Water", "Wood", "Fire", "Earth"];

  const zodiacIndex = (year - 1924) % 12;
  const elementIndex = Math.floor((year % 10) / 2);

  return { animal: animals[zodiacIndex], element: elements[elementIndex] };
};

const toSlug = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function Hero() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Frontend unlock: always show element + zodiac results and detail page button.
  const hasElementsAccess = true;

  const [birthday, setBirthday] = useState(null);
  const [zodiacResult, setZodiacResult] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [flipStep1, setFlipStep1] = useState(false);
  const [flipStep2, setFlipStep2] = useState(false);
  const [flipSave, setFlipSave] = useState(false);
  const [fadeInCalendar, setFadeInCalendar] = useState(false);
  const [textDisplayed, setTextDisplayed] = useState(
    "Search for your Chinese Horoscope Zodiac Animal Sign"
  );

  const [showSubscribe, setShowSubscribe] = useState(false);

  // Save UI
  const [saveMode, setSaveMode] = useState("none"); // none | form | saved
  const [saveName, setSaveName] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  // ✅ prevent duplicate logging
  const lastLogKeyRef = useRef("");

  // Result slug for full detail page
  const resultSlug = useMemo(() => {
    if (!zodiacResult?.animal) return "";
    if (zodiacResult?.element) {
      return `${toSlug(zodiacResult.element)}-${toSlug(zodiacResult.animal)}`;
    }
    return toSlug(zodiacResult.animal);
  }, [zodiacResult]);

  // ✅ helper format for dashboard: DD-MM-YYYY
  const formatDDMMYYYY = (d) => {
    if (!d) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  // ✅ log latest activity (logged-in users)
  const logSearchOnce = async (bday, result) => {
    try {
      if (!user) return; // only logged-in users
      if (!bday || !result?.animal) return;

      const isoDate = bday.toISOString().slice(0, 10); // YYYY-MM-DD
      const displayDate = formatDDMMYYYY(bday); // DD-MM-YYYY

      const animalSlug = toSlug(result.animal);
      const elementSlug = result?.element ? toSlug(result.element) : "";

      const resultUrl = result?.element
        ? `/five-elements-zodiac/${elementSlug}-${animalSlug}`
        : `/chinese-zodiac/${animalSlug}`;

      // Prevent duplicate inserts for same date/url in a single session
      const key = `${isoDate}|${resultUrl}`;
      if (lastLogKeyRef.current === key) return;
      lastLogKeyRef.current = key;

      const resultText = result?.element
        ? `${result.element} ${result.animal}`
        : `${result.animal}`;

      // 1) ✅ always log into search history (for Recent Results)
      const payload = {
        query_type: "birthday",
        input_text: displayDate,
        birthdate: isoDate,
        result_text: resultText,
        result_url: resultUrl,
      };

      await api.post("account/log-search", payload);

      // 2) ✅ ALSO log into activities (for Activities page)
      await api.post("account/activity", {
        type: "search",
        action: `Birthday searched: ${displayDate}`,
        status: "success",
        url: resultUrl,
        details: {
          birthdate: isoDate,
          input_text: displayDate,
          result_text: resultText,
          query_type: "birthday",
        },
      });
    } catch (e) {
      // Do not break UI if backend route not ready
      console.warn("[Hero] log-search/activity failed:", e?.response?.data || e?.message || e);
    }
  };

  useEffect(() => {
    if (zodiacResult && birthday) {
      setTextDisplayed(
        `According to your birthday: ${birthday.toLocaleDateString("en-US", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        })}.`
      );

      setTimeout(() => {
        setShowResults(true);

        // ✅ Log latest activity when result is shown
        logSearchOnce(birthday, zodiacResult);

        confetti({
          particleCount: 180,
          spread: 160,
          startVelocity: 110,
          origin: { y: 0.8 },
          zIndex: 99999,
        });
        setTimeout(() => setShowButtons(true), 900);
      }, 350);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zodiacResult, birthday]);

  const handleDateChange = (date) => {
    setBirthday(date);

    if (date) {
      const birthYear = date.getFullYear();
      const { animal, element } = calculateChineseZodiacAndElement(birthYear);
      setZodiacResult({ animal, element });
    } else {
      setZodiacResult(null);
    }

    setShowCalendar(false);
    setFadeInCalendar(false);
    setTimeout(() => setFlipStep2(true), 300);
  };

  const toggleCalendar = () => {
    setFlipStep1(true);
    setTimeout(() => {
      setFadeInCalendar(true);
      setShowCalendar(true);
    }, 500);
  };

  const goSubscribe = () => navigate("/subscribe");
  const goMonthlyCheckout = () => navigate("/subscribe/checkout?plan=monthly");
  const openSubscribe = () => setShowSubscribe(true);

  const handleLearnMore = () => {
    if (!zodiacResult?.animal) return;

    const animal = toSlug(zodiacResult.animal);
    const element = toSlug(zodiacResult.element);

    if (element) {
      navigate(`/five-elements-zodiac/${element}-${animal}`);
      return;
    }

    navigate(`/chinese-zodiac/${animal}`);
  };

  const handleViewZodiacDetails = () => {
    if (!zodiacResult?.animal) return;
    navigate(`/chinese-zodiac/${toSlug(zodiacResult.animal)}`);
  };

  const handleSearchAnother = () => {
    setFlipStep1(false);
    setFlipStep2(false);
    setFlipSave(false);
    setFadeInCalendar(false);
    setShowCalendar(false);
    setShowResults(false);
    setShowButtons(false);
    setBirthday(null);
    setZodiacResult(null);
    setTextDisplayed("Search for your Chinese Horoscope Zodiac Animal Sign");
    setSaveMode("none");
    setSaveName("");
    setSaveErr("");

    // ✅ allow logging next search
    lastLogKeyRef.current = "";
  };

  // ✅ Save flow
  const openSaveForm = () => {
    setSaveErr("");
    setSaveName("");
    setSaveMode("form");
    setFlipSave(true); // flip to save form
  };

  const cancelSave = () => {
    setSaveErr("");
    setSaveMode("none");
    setFlipSave(false); // flip back to results
  };

  const confirmSaved = () => {
    setSaveMode("saved");
    setFlipSave(true); // keep on save side, but show confirmation panel
  };

  async function submitSave() {
    try {
      setSaveErr("");
      setSaveBusy(true);

      const payload = {
        name: saveName,
        birthday: birthday ? birthday.toISOString() : null,
        animal: zodiacResult?.animal || "",
        element: zodiacResult?.element || "",
        resultSlug: zodiacResult?.element ? resultSlug : "",
      };

      // ✅ backend route we added: POST /birthdays
      const r = await api.post("birthdays", payload);

      if (!r?.data?.ok) throw new Error("Save failed");
      confirmSaved();

      // ✅ log activity for save
      try {
        if (user) {
          await api.post("account/activity", {
            type: "saved",
            action: `Saved birthday: ${formatDDMMYYYY(birthday)}`,
            status: "success",
            url: "/account/saved-birthdays",
            details: {
              name: saveName,
              birthdate: birthday ? birthday.toISOString().slice(0, 10) : null,
              result_text: zodiacResult?.element
                ? `${zodiacResult.element} ${zodiacResult.animal}`
                : `${zodiacResult?.animal || ""}`,
            },
          });
        }
      } catch {}
    } catch (e) {
      setSaveErr("Unable to save right now. Please try again.");
    } finally {
      setSaveBusy(false);
    }
  }

  const goSavedBirthdays = () => navigate("/account/saved-birthdays");

  const TrustLinks = () => (
    <div style={{ marginTop: 10, fontSize: 12, opacity: 0.85, lineHeight: 1.45 }}>
      Secure payments are processed by Stripe. You will be redirected to Stripe Checkout (stripe.com).
      <div style={{ marginTop: 6 }}>
        <a href="/Privacy-Policy" style={{ textDecoration: "underline" }}>Privacy Policy</a>
        {" · "}
        <a href="/Contact" style={{ textDecoration: "underline" }}>Contact</a>
        {" · "}
        <a href="/Aboutus" style={{ textDecoration: "underline" }}>About</a>
      </div>
    </div>
  );

  return (
    <div className={styles.sliderbg}>
      <div
        className={[
          styles.wrapperBox,
          flipStep1 ? styles.flipStep1 : "",
          flipStep2 ? styles.flipStep2 : "",
          flipSave ? styles.flipSave : "",
        ].join(" ")}
      >
        <div className={styles.centerBox}>
          <p className={styles.BoxTitle}>{textDisplayed}</p>

          {!showResults && !birthday && !flipStep1 && (
            <p>
              <input
                type="text"
                onClick={toggleCalendar}
                placeholder="Enter Your Date of Birth"
                readOnly
                value={
                  birthday
                    ? birthday.toLocaleDateString("en-US", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })
                    : ""
                }
                className={styles.dateInput}
              />
            </p>
          )}

          {showCalendar && (
            <div className={`${styles.calendarPopup} ${fadeInCalendar ? styles.fadeIn : ""}`}>
              <DatePicker
                selected={birthday}
                onChange={handleDateChange}
                dateFormat="dd MMMM yyyy"
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
                minDate={new Date(1900, 0, 1)}
                maxDate={new Date(2026, 11, 31)}
                inline
              />
            </div>
          )}

          {showResults && zodiacResult && (
            <div>
              <h2>Your Chinese Zodiac:</h2>

              <div className={styles.imageContainer}>
                {zodiacResult.element ? (
                  <>
                    <img
                      src={`/five-elements-images/${zodiacResult.element.toLowerCase()}-element.png`}
                      alt={`${zodiacResult.element} Element`}
                      className={styles.elementImage}
                    />
                    <img
                      src={`/chinese-zodiac-images/${zodiacResult.animal.toLowerCase()}.png`}
                      alt={`${zodiacResult.animal} Zodiac Sign`}
                      className={styles.animalImage}
                    />
                  </>
                ) : (
                  <img
                    src={`/chinese-zodiac-images/${zodiacResult.animal.toLowerCase()}.png`}
                    alt={`${zodiacResult.animal} Zodiac Sign`}
                    className={styles.animalImage}
                  />
                )}
              </div>

              <h3>
                <p className={styles.result}>
                  {zodiacResult.element
                    ? `${zodiacResult.element} ${zodiacResult.animal}`
                    : `${zodiacResult.animal}`}
                </p>
              </h3>

              {/* ===== Save side (flip) ===== */}
              {saveMode !== "none" && (
                <div className={styles.savePanel}>
                  {saveMode === "form" && (
                    <>
                      <div className={styles.saveTitle}>Save birthday</div>

                      <input
                        className={styles.saveInput}
                        value={saveName}
                        onChange={(e) => setSaveName(e.target.value)}
                        placeholder="Save birthday as name..."
                        autoFocus
                      />

                      {saveErr && <div className={styles.saveError}>{saveErr}</div>}

                      <div className={styles.actions}>
                        <button
                          type="button"
                          className={styles.learnMoreButton}
                          onClick={submitSave}
                          disabled={saveBusy || !saveName.trim()}
                          style={{ background: "#c47a00" }}
                        >
                          {saveBusy ? "Saving…" : "Save"}
                        </button>

                        <button
                          type="button"
                          className={styles.searchAnotherButton}
                          onClick={cancelSave}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}

                  {saveMode === "saved" && (
                    <>
                      <div className={styles.saveTitle}>Birthday saved ✅</div>

                      <div className={styles.actions}>
                        <button
                          type="button"
                          className={styles.learnMoreButton}
                          onClick={goSavedBirthdays}
                          style={{ background: "#c47a00" }}
                        >
                          Go to saved birthdays
                        </button>

                        <button
                          type="button"
                          className={styles.searchAnotherButton}
                          onClick={handleSearchAnother}
                        >
                          Search another birthday
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ===== Unlocked actions for everyone ===== */}
              {showButtons && saveMode === "none" && (
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.learnMoreButton}
                    onClick={handleLearnMore}
                    style={{ background: "#c47a00" }}
                  >
                    View full horoscope page
                  </button>

                  {!!user && (
                    <button
                      type="button"
                      className={styles.saveBirthdayButton}
                      onClick={openSaveForm}
                    >
                      Save this birthday
                    </button>
                  )}

                  <button
                    type="button"
                    className={styles.searchAnotherButton}
                    onClick={handleSearchAnother}
                  >
                    Search another birthday
                  </button>
                </div>
              )}

              {/* Optional upgrade prompt kept available below unlocked results */}
              {!loading && !user && (
                <div className={styles.ctaWrap}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={goSubscribe}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") goSubscribe();
                    }}
                    className={styles.lockBox}
                  >
                    <div className={styles.lockRow}>
                      <div className={styles.lockIcon}>⭐</div>
                      <div className={styles.lockText}>
                        <div className={styles.lockTitle}>Create an account to save birthdays</div>
                        <div className={styles.lockSub}>
                          Search results are unlocked. Sign up or upgrade if you also want to save readings and access premium features.
                        </div>
                      </div>
                    </div>
                  </div>

                  <TrustLinks />
                </div>
              )}

              {!loading && !!user && !user?.subscription?.active && user?.subscription?.plan !== "lifetime" && (
                <div className={styles.ctaWrap}>
                  <div onClick={openSubscribe} className={styles.lockBox}>
                    <div className={styles.lockRow}>
                      <div className={styles.lockIcon}>⭐</div>
                      <div className={styles.lockText}>
                        <div className={styles.lockTitle}>Want premium extras?</div>
                        <div className={styles.lockSub}>
                          Your element result is now visible on the frontend. Upgrade if you still want subscription-only extras elsewhere on the site.
                        </div>
                      </div>
                    </div>
                  </div>

                  {showButtons && saveMode === "none" && (
                    <div className={styles.actions}>
                      <button
                        type="button"
                        className={styles.learnMoreButton}
                        onClick={handleViewZodiacDetails}
                        style={{ background: "#c47a00" }}
                      >
                        View zodiac details
                      </button>

                      <button
                        type="button"
                        className={styles.learnMoreButton}
                        onClick={goMonthlyCheckout}
                        style={{ background: "#c47a00" }}
                      >
                        Continue to secure checkout
                      </button>
                    </div>
                  )}

                  <TrustLinks />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <SubscribeModal open={showSubscribe} onClose={() => setShowSubscribe(false)} />
    </div>
  );
}